import React, { useState, useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Spinner,
  Text,
  useBreakpointValue,
  Center,
} from "@chakra-ui/react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  IconButton,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react";
import { FiFilter } from "react-icons/fi";

const DataTradeEvents = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedActions, setSelectedActions] = useState(["OPENED","CLOSED","INCREASE"]);
  const [visibleRows, setVisibleRows] = useState(9); // Show first 5 rows initially

  const apiUrl = "https://blindfoldcapital.com/api/trade_events/";

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const result = await response.json();
      console.log("Fetched data:", result.data);

      const sortedData = result.data
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .filter((value, index, self) => {
          const currentDate = new Date(value.timestamp);
          const currentHourMinute = `${currentDate.getHours()}:${currentDate.getMinutes()}`;

          return (
            index ===
            self.findIndex((t) => {
              const date = new Date(t.timestamp);
              const hourMinute = `${date.getHours()}:${date.getMinutes()}`;
              return hourMinute === currentHourMinute;
            })
          );
        });

      setData(sortedData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const formatNumber = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? "0.00" : parsed.toFixed(2);
  };

  const loadMore = () => {
    setVisibleRows((prevVisibleRows) => prevVisibleRows + 10);
  };

  const isMobile = useBreakpointValue({ base: true, md: false });

  if (loading)
    return (
      <Center p={4}>
        <Spinner size="sm" />
      </Center>
    );
  if (error) return <Text color="red.500">Error: {error}</Text>;

  return (
    <Box
      bg="gray.200"
      p={1}
      h="400px"
      overflow="auto"
      borderRadius={4}
      w="100%"
      overflowX="auto" 
      border="1px solid ThreeDFace"
    >
      {data.length === 0 ? (
        <Text>No trade events available.</Text>
      ) : (
        <>
          <Table variant="striped" colorScheme="blue" size="sm">
            <Thead>
              <Tr>
                <Th>
                  <Box display="flex" alignItems="center">
                    Action
                    <Popover placement="bottom-start">
                      <PopoverTrigger>
                        <IconButton
                          aria-label="Filter actions"
                          icon={<FiFilter />}
                          size="xs"
                          ml={2}
                          variant="ghost"
                        />
                      </PopoverTrigger>
                      <PopoverContent width="200px">
                        <PopoverArrow />
                        

                        <PopoverBody>
                          <CheckboxGroup
                            colorScheme="blue"
                            size="sm"
                            value={selectedActions}
                            onChange={(values) => setSelectedActions(values)}
                          >
                            <Stack spacing={2}>
                              <Checkbox value="OPENED">Opened</Checkbox>
                              <Checkbox value="CLOSED">Closed</Checkbox>
                              <Checkbox value="INCREASE">Increase</Checkbox>
                            </Stack>
                          </CheckboxGroup>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </Box>
                </Th>

                <Th>IsLong</Th>
                <Th>Token</Th>
                <Th>In</Th>
                {!isMobile && <Th>Size</Th>}
                <Th>Price</Th>
                <Th>Delta</Th>
                <Th>Time</Th>
              </Tr>
            </Thead>
            <Tbody fontStyle={"Sentinel-light"} fontSize="sm">
              {data
                .filter((row) => selectedActions.includes(row.type))
                .slice(0, visibleRows)
                .map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    <Td>{row.type}</Td>
                    <Td>{row.is_long ? "LONG" : "SHORT"}</Td>
                    <Td>{row.token_name}</Td>
                    <Td>${formatNumber(row.collateral_size)}</Td>
                    {!isMobile && <Td>${formatNumber(row.position_size)}</Td>}
                    <Td>${formatNumber(row.price)}</Td>
                    <Td>${formatNumber(row.delta)}</Td>
                    <Td>{formatDate(row.timestamp)}</Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
          {visibleRows < data.length && (
            <Button
              mt={2}
              onClick={loadMore}
              colorScheme="messenger"
              w="100%"
              variant={"outline"}
              size={isMobile ? "sm" : "md"}
            >
              Load More
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default DataTradeEvents;
